/**
 * 贡献视图卡 —— 「这个月我们各自记了多少」（**L 域 L3** · `AC-L3.6`–`L3.10`）
 *
 * 依据：L 域 PRD **§5.3-6**（贡献视图口径）· **§5.3-7**（窗口与单人态）· **§5.3-8**（🔴 红线）·
 * **§7.3**（`AC-L3.7` / `L3.9` / `L3.10`）· `DR-037`（**两桶** · `D2`/`D5`）·
 * 契约 `contract/contribution.ts` · 纯逻辑 `features/contribution.ts`。
 *
 * ## 🔴 本组件**不碰金额、不算日期、不判身份** —— 全部照渲染服务端下发值
 *
 * | 值 | 来源 |
 * |---|---|
 * | 两种桶各多少 | 服务端 `buckets[].expense`（C 域 `composeExpense` 唯一内核） |
 * | 总支出 | 服务端 `totalExpense` —— 🔴 **端侧不求第二次和**（`AC-L3.6` 禁第二份聚合） |
 * | 窗口起止日 | 服务端 `from` / `to`（账本时区），端侧**不拼日历** |
 * | 谁是"我" | 服务端 `isSelf` |
 * | 是否单人账本 | 服务端 `singleMember` —— 🔴 **不数 `buckets.length`**（那是巧合不是判据） |
 *
 * ## 🔴 红线（`AC-L3.9` · 必测）—— 这张卡上**不许出现**的东西
 *
 * 无排名 · 无评分 · 无对比评价 · 无榜单 · 无竞赛元素。
 * 具体到实现：**不渲染任何序号**（①②③ / "第 N 名" / 奖杯 / 皇冠 / 火焰），
 * 文案里**不出现**"多 / 少 / 领先 / 落后 / 超过 / 平均"，也不显示百分比或进度条。
 * 排序（我在首位）只是"先看到自己"，**不是名次** —— 故首行也**不加任何序号**。
 *
 * ## 🔴 两栏的维度不同，必须**分开标注**（`DR-037` D5）
 *
 * 「记录支出」按**记录人**（`transactions.userId`）· 「管理账户数」按**归属人**
 * （`accounts.ownerId`）。把这两栏并成一行"贡献"就是「一条判据两种口径」（违铁律 24）——
 * 所以每栏各自带一句 `hint`，让用户能看出它们量的**不是同一件事**。
 *
 * ## 🔴 单人账本**整块换掉**（`AC-L3.10` / `AC-L7.2`）
 *
 * 不是"隐藏某一行"，而是**整张贡献卡换成邀请引导卡** —— 单人账本里"贡献对比"零语义，
 * 展示它只会让绝大多数（新注册）用户以为自己漏配了什么。文案基调「一起管」。
 */

import { useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import type { ContributionWindow } from '@jiaying/shared';
import { getContribution } from '../api/endpoints.js';
import { Loading } from '../ui/Loading.js';
import { Amount } from '../ui/Amount.js';
import { useBaseCurrency } from '../app/useBaseCurrency.js';
import {
  CONTRIBUTION_WINDOW_OPTIONS,
  toContributionCard,
  type ContributionRow,
} from '../features/contribution.js';

/**
 * 🔴 **不显示序号 / 名次** —— 这个组件就是那条红线的可见载体。
 *
 * 若哪天有人想"给第一名加个皇冠"，改这里就会同时触发 `contribution-card.spec.tsx` 的
 * 反向断言（spec 里有"产物中不得出现 ①/1./第 等序号痕迹"的检查）。
 */
function Row({ row, currency }: { row: ContributionRow; currency: string }) {
  return (
    <Box
      sx={{ py: 1 }}
      data-testid={`contribution-row-${row.isSelf ? 'self' : row.userId}`}
    >
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="body1" sx={{ fontWeight: row.isSelf ? 600 : 400 }} noWrap>
            {row.displayName}
          </Typography>
          {!row.isSelf && (
            <Chip size="small" variant="outlined" label={row.roleLabel} />
          )}
        </Stack>
        {/* 🔴 绝对额不加 `+`（`DR-036` D4）—— 支出是"花了多少"，不是"增加多少"。
            ⚠️ `flexShrink: 0` + `whiteSpace: 'nowrap'` 是**防挤压**，不是排版偏好：
            左列（设置页 `.g2`，1024px 下每列约 464px）里成员名是 `noWrap` 的，
            若金额可收缩/可折行，长名会把 `¥1,234.56` 压成两行、读起来像两个数。 */}
        <Amount
          cents={row.expense as never}
          currency={currency}
          colored={false}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        />
      </Stack>
      <Stack direction="row" spacing={2} sx={{ mt: 0.25 }} flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary">
          {row.expenseHint}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          管理 {row.accountCount} 个账户 · {row.accountHint}
        </Typography>
      </Stack>
    </Box>
  );
}

export function ContributionCard() {
  /**
   * 窗口状态**在端侧**（用户点了什么就是什么），但**窗口 → 日期**一律由服务端算 ——
   * 故这里只存枚举，`from`/`to` 永远来自响应（见 `getContribution()` 的说明）。
   *
   * ⚠️ 变量名**不能叫 `window`** —— 那会遮蔽全局 `window`（本来无害，但一旦有人在本
   * 组件里写 `window.localStorage`，读到的就是那个字符串枚举，报错信息会毫无线索）。
   */
  const [win, setWin] = useState<ContributionWindow | undefined>(undefined);

  /**
   * 🔴 **本币**：贡献视图的金额没有"腿所属账户"这个上下文（它是聚合值），
   * 故币种只能取账本本币。拿不到本币 ⇒ **不渲染金额**（`DR-036` D5 同一条纪律：
   * 金额必带币种，宁可不显示也不伪装成 `¥`）。
   */
  const baseCurrency = useBaseCurrency();

  const q = useQuery({
    queryKey: ['contribution', win ?? 'default'],
    queryFn: () => getContribution(win),
    enabled: baseCurrency !== null,
  });

  if (baseCurrency === null) return <Loading />;
  if (q.isPending) return <Loading />;
  if (q.isError) {
    return <Alert severity="error">贡献视图读取失败：{String(q.error)}</Alert>;
  }

  const model = toContributionCard(q.data);

  // 🔴 单人账本 ⇒ **整块换成邀请引导卡**（`AC-L3.10` ② / `§5.3-7`）——
  //    成员列表本身**不隐藏**（③），邀请入口保留在显眼位置（④）。
  if (model.hidden) {
    return (
      <Card data-testid="contribution-invite-guide" data-anchor="一起记">
        <CardHeader
          title="一起记，账本更完整"
          subheader="一个人也能记，两个人记得更轻松"
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        />
        <CardContent sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary">
            家里的事两个人一起看，谁花了什么一目了然 —— 不用再问「这个月钱去哪了」。
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            邀请入口在「账本设置」里，生成邀请码发给家人即可。
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="contribution-card" data-anchor="这个月">
      <CardHeader
        title="这个月我们各自记了多少"
        subheader={model.rangeText}
        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        subheaderTypographyProps={{ variant: 'caption' }}
        action={
          // 🔴 窗口切换**只改枚举**，日期永远由服务端算（契约 §「窗口」）
          <Stack direction="row" spacing={0.5} sx={{ pt: 1, pr: 1 }} flexWrap="wrap" useFlexGap>
            {CONTRIBUTION_WINDOW_OPTIONS.map((o) => (
              <Chip
                key={o.value}
                size="small"
                label={o.label}
                variant={(win ?? 'thisMonth') === o.value ? 'filled' : 'outlined'}
                onClick={() => setWin(o.value)}
                data-testid={`contribution-window-${o.value}`}
              />
            ))}
          </Stack>
        }
      />
      <Divider />
      <CardContent>
        {model.currencyNotice !== null && (
          <Alert severity="info" sx={{ mb: 1 }} data-testid="contribution-currency-notice">
            {model.currencyNotice}
          </Alert>
        )}

        {model.rows.map((row) => (
          <Row key={row.userId} row={row} currency={baseCurrency} />
        ))}

        <Divider sx={{ my: 1 }} />
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={2}>
          <Typography variant="body2" color="text.secondary">
            合计（{model.windowLabel}）
          </Typography>
          {/* 🔴 直接渲染服务端 `totalExpense` —— 端侧**不做求和**（`AC-L3.6`） */}
          <Amount
            cents={model.totalExpense as never}
            currency={baseCurrency}
            colored={false}
            sx={{ fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap' }}
            data-testid="contribution-total"
          />
        </Stack>

        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
          「记录支出」按<strong>谁记的这笔</strong>统计，「管理账户」按<strong>钱记在谁名下</strong>统计 ——
          两者是不同维度（比如我在你的卡上记一笔，算我记的、不算我的账户）。
        </Typography>
      </CardContent>
    </Card>
  );
}
